import { ENTRY_ANALYTICS_ORGS_MAX, type EntryPhase, PAGE_SIZE_MAX } from "@seagames/contracts";
import { z } from "zod";
import { NOC_SORTS, type NocSort, SPORT_SORTS, STRENGTH_SHOWS, type SportSort, type StrengthShow } from "../../lib/entries/strength";
import { searchText } from "../../lib/list-page/list-search";
import { Select } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, useRouteContext } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "../../lib/api/client";
import { ebnOptions, entryOrganizationsOptions, eoiOptions } from "../../lib/api/entries";
import { errorMessage } from "../../lib/api/messages";
import { canReopenEntries, entriesView } from "../../lib/entries/access";
import { EmptyCard } from "../common/empty-card";
import { StatusGuide } from "../common/status-guide";
import { PageBarButton, usePageBar } from "../shell/page-bar";
import { EntryAnalysisPanel } from "./analysis-panel";
import { InfoIcon } from "../transport/icons";
import { EbnEditor } from "./ebn-editor";
import { EoiEditor } from "./eoi-editor";
import { EntriesOverview } from "./overview";
import { JourneyHeader, type JourneyMode } from "./journey";
import { EditorNotices, HowItWorksSheet, useEntriesGuide } from "./parts";

const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined);

/** The admin NOC page's tabs under the cards: the NOC × sport matrix (+ by event on EBN), the Strength map. */
export const OVERVIEW_TABS = ["matrix", "strength"] as const;
export type OverviewTab = (typeof OVERVIEW_TABS)[number];

/** The NOC cards' sorts (the API's `ENTRY_ORG_SORTS`, with "state" meaning the page's phase). */
export const CARD_SORTS = ["name", "state", "sports", "athletes", "medals", "entries"] as const;
export type CardSort = (typeof CARD_SORTS)[number];
/** The sorts a phase's cards offer: each card shows only its phase's numbers. */
export const cardSortsFor = (phase: EntryPhase): readonly CardSort[] => (phase === "EOI" ? ["name", "state", "sports", "athletes", "medals"] : ["name", "state", "entries"]);

/** Both routes' URL schema; every param lenient (a hand-edited value falls back to unset). */
export const entriesSearchSchema = z.object({
	games_id: lenient(z.string().uuid()),
	org: lenient(z.string().uuid()),
	ticked: lenient(z.boolean()),
	search: lenient(searchText()),
	page: lenient(z.coerce.number().int().min(1)),
	page_size: lenient(z.coerce.number().int().min(1).max(PAGE_SIZE_MAX)),
	sport: lenient(z.string().uuid()),
	view: lenient(z.enum(["strength"])),
	show: lenient(z.enum(STRENGTH_SHOWS)),
	sport_sort: lenient(z.enum(SPORT_SORTS)),
	noc_sort: lenient(z.enum(NOC_SORTS)),
	// Admin statistics: the NOC filter (comma list of ids; malformed ids are dropped by `parseIdList`) and the tab under the charts.
	nocs: lenient(z.string().max(ENTRY_ANALYTICS_ORGS_MAX * 37)),
	tab: lenient(z.enum(OVERVIEW_TABS)),
	sort: lenient(z.enum(CARD_SORTS)),
	dir: lenient(z.enum(["asc", "desc"])),
});

/**
 * The routes are `contentWidth: "full"` for the admin NOC table; an editor
 * keeps the usual page column (her `.pg`: 1176px of content, centred).
 */
const PAGE_COLUMN = "mx-auto w-full min-w-0 max-w-[73.5rem]";

/** The URL state of `/accreditation/entries/eoi` and `/ebn`. */
export interface EntriesSearch {
	games_id?: string;
	/** The NOC being viewed (admins / host groups pick it from the overview; a NOC with one needs none). */
	org?: string;
	/** EOI: show only the ticked sports. */
	ticked?: boolean;
	/** Overview: NOC search, page, page size, the by-event sport. */
	search?: string;
	page?: number;
	page_size?: number;
	sport?: string;
	/** Overview (admins, SEA Games): the Strength map instead of the NOC table; its view options. */
	view?: "strength";
	show?: StrengthShow;
	sport_sort?: SportSort;
	noc_sort?: NocSort;
	/** Overview (admins): the statistics' NOC filter as a comma list of organisation ids (`sport` is its sport filter). */
	nocs?: string;
	/** Overview (admins): the tab under the statistics; `view=strength` (older links) still opens the Strength map. */
	tab?: OverviewTab;
	/** Overview: the NOC cards' order (unset = name A→Z) and its direction (unset = the key's own). */
	sort?: CardSort;
	dir?: "asc" | "desc";
}

/**
 * Sport entries (M7b): one route per phase. Who sees what is the ROLE
 * (`entriesView`): a NOC gets its own editor; admins and host groups get the
 * NOC overview, and the same editor for one NOC once one is picked (`org`),
 * which is editing on behalf. The API scopes every read and judges every
 * write; this only picks the page.
 */
export function EntriesPage({
	phase,
	gamesId,
	search,
	patch,
}: {
	phase: EntryPhase;
	gamesId: string;
	search: EntriesSearch;
	patch: (next: Partial<EntriesSearch>) => void;
}) {
	const { session } = useRouteContext({ from: "/_app" });
	const view = entriesView(session);
	if (view === "noc") return <NocEntries phase={phase} gamesId={gamesId} search={search} patch={patch} />;
	if (!search.org) return <EntriesOverview phase={phase} gamesId={gamesId} search={search} patch={patch} admin={view === "admin"} />;
	return <OrgEntries phase={phase} gamesId={gamesId} orgId={search.org} search={search} patch={patch} onBehalf />;
}

/** A NOC: its organisations from `/entries/organizations` — one is the usual case; several get a picker. */
function NocEntries({ phase, gamesId, search, patch }: { phase: EntryPhase; gamesId: string; search: EntriesSearch; patch: (next: Partial<EntriesSearch>) => void }) {
	const { t } = useTranslation("entries");
	const orgs = useQuery(entryOrganizationsOptions({ games_id: gamesId, page: 1, page_size: 100 }));
	const items = orgs.data?.items ?? [];
	const orgId = search.org ?? (items.length === 1 ? items[0]?.organization.id : undefined);
	if (orgs.isPending)
		return (
			<div className={PAGE_COLUMN}>
				<EditorSkeleton />
			</div>
		);
	if (orgs.isError)
		return (
			<div className={PAGE_COLUMN}>
			<EmptyCard
				tone="bad"
				title={t("common.loadFailed")}
				actions={
					<button type="button" className="btn btn-sm btn-line" onClick={() => void orgs.refetch()}>
						{t("common.tryAgain")}
					</button>
				}
			>
				{errorMessage(orgs.error)}
			</EmptyCard>
			</div>
		);
	if (items.length === 0)
		return (
			<div className={PAGE_COLUMN}>
				<EmptyCard title={t("page.noOrgTitle")}>{t("page.noOrgHint")}</EmptyCard>
			</div>
		);
	const picker =
		items.length > 1 ? (
			<Select
				size="sm"
				aria-label={t("page.pickOrg")}
				placeholder={t("page.pickOrg")}
				value={orgId ?? null}
				onChange={(v) => patch({ org: v ?? undefined })}
				options={items.map((o) => ({ value: o.organization.id, label: o.organization.name }))}
				className="w-full max-w-[22rem]"
			/>
		) : null;
	if (!orgId)
		return (
			<div className={`${PAGE_COLUMN} flex flex-col gap-[1rem]`}>
				{picker}
				<EmptyCard title={t("page.pickTitle")}>{t("page.pickHint")}</EmptyCard>
			</div>
		);
	return <OrgEntries key={orgId} phase={phase} gamesId={gamesId} orgId={orgId} search={search} patch={patch} picker={picker} />;
}

/**
 * One NOC's editor for one phase, under the step strip. Both views are read
 * (each ONE statement, cached): the strip needs both phases' state and
 * window, and the EBN page the EOI's.
 */
function OrgEntries({
	phase,
	gamesId,
	orgId,
	search,
	patch,
	onBehalf = false,
	picker,
}: {
	phase: EntryPhase;
	gamesId: string;
	orgId: string;
	search: EntriesSearch;
	patch: (next: Partial<EntriesSearch>) => void;
	onBehalf?: boolean;
	picker?: React.ReactNode;
}) {
	const { t } = useTranslation("entries");
	const { session } = useRouteContext({ from: "/_app" });
	const [help, setHelp] = useState(false);
	const scope = { games_id: gamesId, organization_id: orgId };
	const eoi = useQuery(eoiOptions(scope));
	const ebn = useQuery(ebnOptions(scope));
	const guide = useEntriesGuide();
	const asAdmin = entriesView(session) === "admin";
	const orgName = eoi.data?.organization.name ?? ebn.data?.organization.name;
	const route = phase === "EOI" ? "/accreditation/entries/eoi" : "/accreditation/entries/ebn";

	usePageBar({
		subtitle: orgName ? t("page.subtitle", { org: orgName }) : undefined,
		back: onBehalf ? { to: route, search: { games_id: search.games_id }, label: t("page.back") } : undefined,
		actions: <PageBarButton icon={<InfoIcon />} label={t("help.button")} onClick={() => setHelp(true)} />,
	});

	// The EBN is only readable once the EOI is final; an older API may answer 409 instead of a view.
	const ebnBlocked = ebn.error instanceof ApiError && ebn.error.code === "EOI_NOT_FINAL";
	const eoiStep = eoi.data ? { state: eoi.data.submission.state, window: eoi.data.window } : undefined;
	const ebnStep = ebn.data
		? { state: ebn.data.submission.state, window: ebn.data.window }
		: eoi.data && ebnBlocked
			? { state: eoi.data.ebn_state, window: { open: false, closes_at: null, next_opens_at: null } }
			: undefined;
	const current = phase === "EOI" ? eoi : ebn;
	const links = { games_id: search.games_id, org: search.org };
	const shown = phase === "EOI" ? eoi.data : ebn.data;
	const mode: JourneyMode =
		phase === "EBN" && (ebnBlocked || (ebn.data && ebn.data.eoi_state !== "FINAL"))
			? "waiting"
			: shown?.submission.state === "FINAL"
				? "final"
				: shown && !shown.can_edit
					? "readOnly"
					: "edit";

	return (
		<div className={`${PAGE_COLUMN} flex flex-col gap-[0.875rem]`}>
			{picker}
			<JourneyHeader
				current={phase}
				mode={mode}
				orgName={orgName}
				eoi={eoiStep}
				ebn={ebnStep}
				search={links}
				aside={<StatusGuide title={guide.title} groups={guide.groups} />}
			/>
			{asAdmin && <EntryAnalysisPanel gamesId={gamesId} orgId={orgId} />}

			{current.isPending && !(phase === "EBN" && ebnBlocked) ? (
				<EditorSkeleton />
			) : phase === "EBN" && (ebnBlocked || (ebn.data && ebn.data.eoi_state !== "FINAL")) ? (
				<EmptyCard
					title={t("ebn.needsEoiTitle")}
					actions={
						<Link to="/accreditation/entries/eoi" search={{ games_id: search.games_id, org: search.org }} className="btn btn-sm btn-line">
							{t("ebn.goEoi")}
						</Link>
					}
				>
					{t("ebn.needsEoiHint")}
				</EmptyCard>
			) : current.isError ? (
				<EmptyCard
					tone="bad"
					title={t("common.loadFailed")}
					actions={
						<button type="button" className="btn btn-sm btn-line" onClick={() => void current.refetch()}>
							{t("common.tryAgain")}
						</button>
					}
				>
					{errorMessage(current.error)}
				</EmptyCard>
			) : phase === "EOI" && eoi.data ? (
				<>
					<EditorNotices submission={eoi.data.submission} window={eoi.data.window} canEdit={eoi.data.can_edit} asAdmin={asAdmin} onBehalfOf={onBehalf ? eoi.data.organization.name : null} />
					<EoiEditor
						key={`${eoi.data.organization.id}|${eoi.data.games_id}`}
						view={eoi.data}
						tickedOnly={search.ticked ?? false}
						onTickedOnly={(v) => patch({ ticked: v || undefined })}
						canReopen={canReopenEntries(session)}
						search={links}
					/>
				</>
			) : phase === "EBN" && ebn.data ? (
				<>
					<EditorNotices submission={ebn.data.submission} window={ebn.data.window} canEdit={ebn.data.can_edit} asAdmin={asAdmin} onBehalfOf={onBehalf ? ebn.data.organization.name : null} />
					<EbnEditor key={`${ebn.data.organization.id}|${ebn.data.games_id}`} view={ebn.data} canReopen={canReopenEntries(session)} search={links} />
				</>
			) : null}
			<HowItWorksSheet open={help} onClose={() => setHelp(false)} />
		</div>
	);
}

export function EditorSkeleton() {
	return (
		<div className="grid gap-[0.875rem]" aria-busy>
			<div className="skeleton h-[4.75rem] rounded-[0.625rem]" />
			<div className="skeleton h-[18rem] rounded-[0.625rem]" />
		</div>
	);
}
